import React, { Component, forwardRef } from 'react';
import { FixedSizeList as List } from 'react-window';
import strings from '../locales/';

class AutocompleteFilter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filterText: '',
      filteredOptions: []
    }

    // References for list dimensions
    this.listHeight = 256; // Maximum height of the list
    this.itemHeight = 22; // Height of each item

    // For controlling the timer
    this.resetFilterTimer = null;
  }

  componentWillUnmount() {
    // Cleanup timer when component unmounts
    if (this.resetFilterTimer) {
      clearTimeout(this.resetFilterTimer);
      this.resetFilterTimer = null;
    }
  }

  /**
   * Updates the filtered list when props or state change.
   * If props change, it searches for options that match the current filter text.
   * @param {object} props - The new props of the component
   * @param {object} state - The current state of the component
   * @returns {object} The new state of the component
   */
  static getDerivedStateFromProps(props, state) {
    if (!props.options) return null;

    const filteredOptions = props.options.filter(option => {
      if (option.key === '' || state.filterText === '') return true;
      return option.value.toUpperCase().indexOf(state.filterText.toUpperCase()) >= 0;
    });

    return {
      filteredOptions
    };
  }

  /**
   * Finds and returns the label corresponding to the selected key
   * @param {string|number} key - Key to search for in the options
   * @returns {string} The corresponding label or empty string if not found
   */
  findSelectedLabel = (key) => {
    if (this.props.options) {
      for (var i = 0; i < this.props.options.length; i++) {
        if (this.props.options[i].key == key) {
          return this.props.options[i].value;
        }
      }
    }
    return "";
  };

  /**
   * Updates the state with filter text entered by the user
   * @param {Event} event - Input change event
   */
  changeFilter = (event) => {
    this.setState({
      filterText: event.target.value
    });
  };

  /**
   * Resets the filter text after a specified delay
   * @param {Event} event - Input blur event
   */
  resetFilter = (event) => {
    // Save reference to timer so it can be cancelled if needed
    this.resetFilterTimer = setTimeout(() => {
      this.setState({
        filterText: ''
      });
    }, 1000);
  };

  /**
   * Generates dynamic data- attributes based on configuration
   * @param {Object} option - Current dropdown option
   * @param {number} index - Index of the option in the list
   * @returns {Object} Object with data attributes for the element
   */
  generateDataAttributes = (option, index) => {
    const dataAttributes = {};

    // If additional data attributes were provided
    if (this.props.dataAttributes && typeof this.props.dataAttributes === 'object') {
      // Iterate through each defined data attribute
      Object.entries(this.props.dataAttributes).forEach(([dataAttr, valueConfig]) => {
        // Determine the value based on configuration type
        let attributeValue;

        if (typeof valueConfig === 'function') {
          // If it's a function, execute it with the option as parameter
          try {
            attributeValue = valueConfig(option, index);
          } catch (error) {
            console.error(`Error executing function for ${dataAttr}:`, error);
          }
        } else {
          // For any other type, use the value directly
          attributeValue = valueConfig;
        }

        // Only add the attribute if the value is not undefined
        if (attributeValue !== undefined) {
          dataAttributes[dataAttr] = attributeValue;
        }
      });
    }

    return dataAttributes;
  };

  /**
   * Renders an individual item from the virtualized list
   * @param {Object} params - Parameters provided by react-window
   * @param {number} params.index - Index of the item in the list
   * @param {Object} params.style - Positioning styles provided by react-window
   * @returns {JSX.Element} Rendered list item
   */
  renderRow = ({ index, style }) => {
    // Directly use the filtered list from state
    const option = this.state.filteredOptions[index];

    // Generate dynamic data attributes
    const dataAttributes = this.generateDataAttributes(option, index);

    return (
      <div
        key={index}
        style={{ ...style }}
        className="dropdown-option"
        {...dataAttributes}
        onClick={this.props.onChange}
      >
        {option.value}
      </div>
    );
  };

  /**
   * Renders the virtualized list using react-window to optimize performance
   * Only renders visible elements instead of the entire list
   * @returns {JSX.Element|null} react-window List component or null if no options
   */
  renderVirtualizedList = () => {
    const { filteredOptions } = this.state;

    // If no options, don't render the list
    if (!filteredOptions || filteredOptions.length === 0) {
      return null;
    }

    // Calculate the actual height of the list (limit maximum height)
    const height = Math.min(this.listHeight, filteredOptions.length * this.itemHeight);

    return (
      <List
        className="virtualized-list"
        height={height}
        itemCount={filteredOptions.length}
        itemSize={this.itemHeight}
        width="100%"
        outerElementType={CustomOuterElement}
      >
        {this.renderRow}
      </List>
    );
  };

  /**
   * Renders the complete autocomplete filter component
   * @returns {JSX.Element} The complete AutocompleteFilter component
   */
  render() {
    return (
      <div className={`dropdown-container ${this.props.className ? this.props.className : ''}`}>
        <div className="dropdown-header">
          <div id={this.props.id} className="dropdown-title-group">
            <span className="dropdown-title">{this.props.label + ": "}</span>
            <div className="dropdown-subtitle">{this.findSelectedLabel(this.props.selectedKey)}</div>
          </div>
          <div className="dropdown-icon">
            <i className="fas fa-caret-down" />
          </div>
        </div>
        <div className="dropdown-menu">
          <input
            className="dropdown-search"
            type="text"
            placeholder={strings.filter.search}
            value={this.state.filterText}
            onChange={this.changeFilter}
            onBlur={this.resetFilter}
          />
          <div className="dropdown-options">
            {this.props.options && this.renderVirtualizedList()}
          </div>
        </div>
      </div>
    );
  }
}

export default AutocompleteFilter;

/**
 * Custom element for the outer container of the virtualized list
 * Controls the scroll event to prevent propagation
 */
const CustomOuterElement = forwardRef((props, ref) => (
  <div
    {...props}
    ref={ref}
    onScroll={(e) => {
      e.stopPropagation(); // stop event propagation
      if (props.onScroll) {
        props.onScroll(e);
      }
    }}
  />
));



// WEBPACK FOOTER //
// ./source/client/components/common/autocompleteFilter.jsx